import React, { useState } from 'react';
import { FiSearch, FiCheck, FiAlertCircle } from 'react-icons/fi';

// CEPs de Barueri: 06401-000 a 06499-999
const BARUERI_CEP_RANGE = {
  min: 6401000,
  max: 6499999,
  city: 'Barueri'
};

const CepInput = ({ onAddressFound, disabled = false }) => {
  const [cep, setCep] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [address, setAddress] = useState(null);

  const formatCep = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    // Formata como 00000-000
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  const isBarueriCep = (cepNumber) => {
    const num = parseInt(cepNumber.replace(/\D/g, ''), 10);
    return num >= BARUERI_CEP_RANGE.min && num <= BARUERI_CEP_RANGE.max;
  };

  const handleCepChange = (e) => {
    const formatted = formatCep(e.target.value);
    setCep(formatted);
    setError('');
    setAddress(null);
  };

  const searchCep = async () => {
    const cleanCep = cep.replace(/\D/g, '');
    
    if (cleanCep.length !== 8) {
      setError('CEP deve ter 8 dígitos');
      return;
    }

    // Validar se é CEP de Barueri
    if (!isBarueriCep(cleanCep)) {
      setError('Desculpe, só atendemos a cidade de Barueri (CEPs 06401-000 a 06499-999)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        setError('CEP não encontrado');
        return;
      }

      // Verificar se é realmente Barueri
      if (data.localidade?.toLowerCase() !== 'barueri') {
        setError('Desculpe, só atendemos a cidade de Barueri');
        return;
      }

      const fullAddress = {
        cep: data.cep,
        street: data.logradouro,
        neighborhood: data.bairro,
        city: data.localidade,
        state: data.uf,
        fullAddress: `${data.logradouro}${data.logradouro ? ', ' : ''}${data.bairro} - ${data.localidade}/${data.uf}`
      };

      setAddress(fullAddress);
      if (onAddressFound) {
        onAddressFound(fullAddress);
      }
    } catch (err) {
      setError('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchCep();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={cep}
            onChange={handleCepChange}
            onKeyPress={handleKeyPress}
            placeholder="00000-000"
            maxLength={9}
            disabled={disabled || loading}
            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none ${
              error ? 'border-red-300 bg-red-50' : 
              address ? 'border-green-300 bg-green-50' : 
              'border-gray-300'
            }`}
          />
          {address && (
            <FiCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600" />
          )}
        </div>
        <button
          type="button"
          onClick={searchCep}
          disabled={disabled || loading || cep.replace(/\D/g, '').length !== 8}
          className="px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <FiSearch />
          )}
          Buscar
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
          <FiAlertCircle className="flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {address && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3">
          <p className="text-sm text-green-700 font-medium">📍 Endereço encontrado:</p>
          <p className="text-green-800 mt-1">{address.fullAddress}</p>
        </div>
      )}

      <p className="text-xs text-gray-500">
        ℹ️ Atendemos apenas a cidade de Barueri/SP
      </p>
    </div>
  );
};

export default CepInput;
